import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Statistic,
  Descriptions,
  Tabs,
  Table,
  Progress,
  Avatar,
  Typography,
  Divider,
  Alert,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  InboxOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// Mock Google Maps component (replace with actual Google Maps integration)
const LocationMap = ({ location }) => {
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => setMapLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!mapLoaded) {
    return (
      <div style={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px solid #d9d9d9',
        borderRadius: 6
      }}>
        <Spin size="large" />
        <Text style={{ marginLeft: 12 }}>Loading map...</Text>
      </div>
    );
  }

  return (
    <div style={{ 
      height: 400, 
      backgroundColor: '#f0f2f5', 
      border: '1px solid #d9d9d9',
      borderRadius: 6,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Mock map interface */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e8e8e8' fill-opacity='0.3'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <EnvironmentOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4} style={{ margin: 0, marginBottom: 8 }}>{location?.name}</Title>
          <Text type="secondary">{location?.address}</Text>
          <br />
          <Text type="secondary">{location?.city}, {location?.state} {location?.zipCode}</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" icon={<EnvironmentOutlined />}>
              Get Directions
            </Button>
          </div>
        </div>
      </div>
      
      {/* Map controls */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
      }}>
        <Button size="small" style={{ backgroundColor: 'white' }}>+</Button>
        <Button size="small" style={{ backgroundColor: 'white' }}>-</Button>
      </div>
    </div>
  );
};

const LocationDetailView = ({ location, onBack, onEdit }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!location) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14' }} />
          <Title level={4} style={{ marginTop: 16 }}>Location not found</Title>
          <Button type="primary" onClick={onBack}>
            Go Back
          </Button>
        </div>
      </Card>
    );
  }

  // Mock inventory data
  const inventoryData = [
    {
      key: '1',
      product: 'Barcode Scanner Pro',
      sku: 'BSCN-001',
      quantity: 25,
      reserved: 5,
      available: 20,
      reorderPoint: 10,
      status: 'In Stock'
    },
    {
      key: '2',
      product: 'Qodequay Tech',
      sku: '001',
      quantity: 8,
      reserved: 2,
      available: 6,
      reorderPoint: 10,
      status: 'Low Stock'
    },
    {
      key: '3',
      product: 'prod 1',
      sku: '010',
      quantity: 0,
      reserved: 0,
      available: 0,
      reorderPoint: 5,
      status: 'Out of Stock'
    }
  ];

  const inventoryColumns = [
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>SKU: {record.sku}</Text>
        </div>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity) => (
        <Text strong style={{ fontSize: 16 }}>{quantity}</Text>
      ),
    },
    {
      title: 'Available',
      dataIndex: 'available',
      key: 'available',
      width: 100,
      render: (available, record) => (
        <div>
          <Text>{available}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Reserved: {record.reserved}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const getStatusConfig = (status) => {
          switch (status) {
            case 'In Stock':
              return { color: 'green', icon: <CheckCircleOutlined /> };
            case 'Low Stock':
              return { color: 'orange', icon: <WarningOutlined /> };
            case 'Out of Stock':
              return { color: 'red', icon: <ExclamationCircleOutlined /> };
            default:
              return { color: 'default', icon: null };
          }
        };
        
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {status}
          </Tag>
        );
      },
    },
  ];

  const getLocationStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'maintenance': return 'orange';
      default: return 'default';
    }
  };

  const getLocationTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'warehouse': return <InboxOutlined />;
      case 'store': return <ShopOutlined />;
      case 'office': return <UserOutlined />;
      default: return <EnvironmentOutlined />;
    }
  };

  return (
    <div style={{ padding: '0 24px' }}>
      {/* Back Button */}
      <div style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          type="text"
          size="large"
        >
          Back to Locations
        </Button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              {getLocationTypeIcon(location.type)}
              <Title level={2} style={{ margin: 0 }}>
                {location.name}
              </Title>
              <Tag color={getLocationStatusColor(location.status)}>
                {location.status || 'Active'}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => onEdit(location)}
              >
                Edit Location
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Location Info & Map */}
        <Col xs={24} lg={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Location Overview Card */}
            <Card title="Location Overview">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic 
                    title="Total Products" 
                    value={inventoryData.length}
                    prefix={<InboxOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Total Quantity" 
                    value={inventoryData.reduce((sum, item) => sum + item.quantity, 0)}
                    prefix={<ShopOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Low Stock Items" 
                    value={inventoryData.filter(item => item.status === 'Low Stock' || item.status === 'Out of Stock').length}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Map Card */}
            <Card title={
              <Space>
                <EnvironmentOutlined />
                Location Map
              </Space>
            }>
              <LocationMap location={location} />
            </Card>
          </Space>
        </Col>

        {/* Right Column - Details */}
        <Col xs={24} lg={10}>
          <Card title="Location Details">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Type">
                <Space>
                  {getLocationTypeIcon(location.type)}
                  {location.type || 'Warehouse'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {location.address}
              </Descriptions.Item>
              <Descriptions.Item label="City">
                {location.city}
              </Descriptions.Item>
              <Descriptions.Item label="State">
                {location.state}
              </Descriptions.Item>
              <Descriptions.Item label="ZIP Code">
                {location.zipCode}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {location.country || 'United States'}
              </Descriptions.Item>
              <Descriptions.Item label="Manager">
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  {location.manager || 'John Doe'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Contact">
                <Space direction="vertical" size="small">
                  <Space>
                    <PhoneOutlined />
                    <Text>{location.phone || '(555) 123-4567'}</Text>
                  </Space>
                  <Space>
                    <MailOutlined />
                    <Text>{location.email || 'location@company.com'}</Text>
                  </Space>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Operating Hours">
                <Space>
                  <ClockCircleOutlined />
                  <Text>{location.hours || 'Mon-Fri: 9AM-6PM'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {location.createdAt ? new Date(location.createdAt).toLocaleDateString() : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Inventory Tab */}
      <Card style={{ marginTop: 24 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Inventory" key="inventory">
            <Table
              columns={inventoryColumns}
              dataSource={inventoryData}
              pagination={false}
              size="small"
            />
          </TabPane>
          <TabPane tab="Activity Log" key="activity">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <ClockCircleOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <Title level={4} type="secondary" style={{ marginTop: 16 }}>
                Activity Log
              </Title>
              <Text type="secondary">Recent location activities will appear here</Text>
            </div>
          </TabPane>
          <TabPane tab="Settings" key="settings">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={4} type="secondary">Location Settings</Title>
              <Text type="secondary">Location-specific configuration options</Text>
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default LocationDetailView;
